/**
 * Preview Canvas Component
 * Read-only preview of lesson content as it would appear in the exported course player.
 * Uses the same shared renderers as exported packages for accurate preview.
 * Requirements: 7.2, 7.3
 *
 * Progressive scroll display:
 * - Block reveal is disabled by default to match the exported player
 * - Content after blocking videos is completely hidden until video is completed
 */

import { useMemo, useCallback, useEffect } from 'react';
import { designTokens, isBlockSuppressed } from '@course-authoring/shared-types';
import type { CourseBlock, LessonThemeColor } from '@course-authoring/shared-types';
import { CourseCanvas, useBlockPacing, type BlockPacing, type VideoTranscriptOperations } from '@course-authoring/ui-blocks';
import { useEditorStore } from '../store/editorStore';
import { checkTranscriptAvailability, downloadTranscript } from '../api/centerstageApi';

const previewTranscriptOperations: VideoTranscriptOperations = {
  checkAvailability: checkTranscriptAvailability,
  download: downloadTranscript,
};

interface LessonPreviewCanvasProps {
  pacing?: BlockPacing;
  blocks: CourseBlock[];
  completionStatus: Record<string, boolean>;
  onBlockComplete: (blockId: string) => void;
  enableCompletionTracking: boolean;
  enableSuccessAnimations: boolean;
  lessonTitle?: string;
  lessonThemeColor?: LessonThemeColor;
  currentLocale?: string;
  /** NA-408 "Show Block IDs": course id, omitted when there is no course context. */
  courseNumericId?: number;
  /** NA-408: lesson id for the block id string. */
  lessonNumericId?: number;
  /** NA-408: locale of the text actually rendered, distinct from `currentLocale`. */
  lessonLocale?: string;
  /** NA-408: learner has turned on "Show Block IDs" in the account popover. */
  showBlockIds?: boolean;
  lessonIndex?: number;
  totalLessons?: number;
  previewTargetBlockId?: string | null;
  onPreviewTargetHandled?: () => void;
  enableBlockReveal?: boolean;
  topContent?: React.ReactNode;
  onLessonCompleteContinue?: () => void;
  footerContent?: React.ReactNode;
  onEngage?: () => void;
}

export function LessonPreviewCanvas({
  pacing: suppliedPacing,
  blocks,
  completionStatus,
  onBlockComplete,
  enableCompletionTracking,
  enableSuccessAnimations,
  lessonTitle,
  lessonThemeColor,
  currentLocale,
  courseNumericId,
  lessonNumericId,
  lessonLocale,
  showBlockIds,
  lessonIndex,
  totalLessons,
  previewTargetBlockId,
  onPreviewTargetHandled,
  // Defaults to false so every preview context matches the exported player,
  // which renders with block reveal disabled (see apps/player/src/ExportApp.tsx).
  enableBlockReveal = false,
  topContent,
  onLessonCompleteContinue,
  footerContent,
  onEngage,
}: LessonPreviewCanvasProps) {
  const localPacing = useBlockPacing(JSON.stringify(blocks.map((block) => block.id)));
  const pacing = suppliedPacing ?? localPacing;
  useEffect(() => {
    if (previewTargetBlockId) pacing.revealThrough(blocks.map((block) => block.id), previewTargetBlockId);
  }, [pacing, blocks, previewTargetBlockId]);
  useEffect(() => {
    if (!previewTargetBlockId) return;

    const timeout = setTimeout(() => {
      const el = document.querySelector(`[data-block-id="${previewTargetBlockId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      onPreviewTargetHandled?.();
    }, 500);

    return () => clearTimeout(timeout);
  }, [previewTargetBlockId, onPreviewTargetHandled, blocks]);

  return (
    <CourseCanvas
      pacing={pacing}
      blocks={blocks}
      completionStatus={completionStatus}
      onBlockComplete={onBlockComplete}
      enableCompletionTracking={enableCompletionTracking}
      enableSuccessAnimations={enableSuccessAnimations}
      enableBlockReveal={enableBlockReveal}
      topContent={topContent}
      lessonTitle={lessonTitle}
      lessonThemeColor={lessonThemeColor}
      currentLocale={currentLocale}
      courseNumericId={courseNumericId}
      lessonNumericId={lessonNumericId}
      lessonLocale={lessonLocale}
      showBlockIds={showBlockIds}
      transcriptOperations={previewTranscriptOperations}
      lessonIndex={lessonIndex}
      totalLessons={totalLessons}
      onLessonCompleteContinue={onLessonCompleteContinue}
      footerContent={footerContent}
      onEngage={onEngage}
      style={styles.canvas}
      emptyState={
        <div style={styles.canvas}>
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>👁️</div>
            <h2 style={styles.emptyTitle}>Preview Mode</h2>
            <p style={styles.emptyText}>
              Add blocks to the lesson to preview content.
            </p>
          </div>
        </div>
      }
    />
  );
}

export function PreviewCanvas({
  pacing,
  footerContent,
  currentLocale,
  courseNumericId,
  lessonNumericId,
  lessonLocale,
  showBlockIds,
  topContent,
}: {
  pacing?: BlockPacing;
  footerContent?: React.ReactNode;
  currentLocale?: string;
  courseNumericId?: number;
  lessonNumericId?: number;
  lessonLocale?: string;
  showBlockIds?: boolean;
  topContent?: React.ReactNode;
} = {}) {
  // Get blocks data
  const allBlocks = useEditorStore((state) => state.blocks);
  const blockOrder = useEditorStore((state) => state.blockOrder);
  const lessonTitle = useEditorStore((state) => state.lessonMeta.title);
  const lessonThemeColor = useEditorStore((state) => state.lessonMeta.themeColor);
  const isPreviewMode = useEditorStore((state) => state.isPreviewMode);
  const previewBlockCompletion = useEditorStore((state) => state.previewBlockCompletion);
  const markPreviewBlockComplete = useEditorStore((state) => state.markPreviewBlockComplete);
  const markPreviewEngaged = useEditorStore((state) => state.markPreviewEngaged);
  const previewTargetBlockId = useEditorStore((state) => state.previewTargetBlockId);
  const setPreviewTargetBlockId = useEditorStore((state) => state.setPreviewTargetBlockId);

  // Derive blocks array. In client context, blocks the account has suppressed
  // are omitted from the learner preview (they show greyed-out in the editor
  // list only).
  const blocks = useMemo(() => {
    return blockOrder
      .map((blockId) => allBlocks[blockId])
      .filter((block): block is CourseBlock => block !== undefined)
      .filter((block) => !isBlockSuppressed(block));
  }, [blockOrder, allBlocks]);

  // Handle block completion
  // Requirements: 7.2
  const handleBlockComplete = useCallback(
    (blockId: string) => {
      markPreviewBlockComplete(blockId);
    },
    [markPreviewBlockComplete]
  );

  return (
    <LessonPreviewCanvas
      pacing={pacing}
      blocks={blocks}
      completionStatus={previewBlockCompletion}
      onBlockComplete={handleBlockComplete}
      enableCompletionTracking={isPreviewMode}
      enableSuccessAnimations={isPreviewMode}
      lessonTitle={lessonTitle}
      lessonThemeColor={lessonThemeColor}
      currentLocale={currentLocale}
      courseNumericId={courseNumericId}
      lessonNumericId={lessonNumericId}
      lessonLocale={lessonLocale}
      showBlockIds={showBlockIds}
      topContent={topContent}
      previewTargetBlockId={previewTargetBlockId}
      onPreviewTargetHandled={() => setPreviewTargetBlockId(null)}
      footerContent={footerContent}
      onEngage={markPreviewEngaged}
    />
  );
}

const styles: Record<string, React.CSSProperties> = {
  canvas: {
    flex: 1,
    height: '100%',
    backgroundColor: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  emptyState: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px',
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  emptyTitle: {
    margin: '0 0 8px',
    fontSize: '20px',
    fontWeight: 600,
    color: designTokens.colors.textPrimary,
  },
  emptyText: {
    margin: 0,
    fontSize: '14px',
    color: designTokens.colors.textSecondary,
  },
};
